import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { reportsAPI } from '../../services/apiServices';
import { Alert, Spinner } from '../../components/common/UI';
import DateField from '../../components/common/DateField';
import MoneyValue from '../../components/common/MoneyValue';
import { getDerivedBusinessStatus, PROGRESS_STATUS_KEYS } from '../../utils/businessStatus';
import { getSubscriptionRegularizationMessage, isSubscriptionLock } from '../../utils/plans';
import { formatLocalizedDate } from '../../utils/dateUtils';
import { getApiErrorMessage } from '../../utils/apiErrors';
import { useAuth } from '../../contexts/AuthContext';
import { getBusinessClientName } from '../../utils/businessOwner';
import { formatMoneyNumber } from '../../utils/currency';
import {
  BarChartIcon,
  BriefcaseIcon,
  CalendarIcon,
  ClockIcon,
  CreditCardIcon,
  UsersIcon,
} from '../../components/common/Icons';

const TABS = [
  { id: 'clients', labelKey: 'reports.tabs.contacts', icon: UsersIcon },
  { id: 'businesses', labelKey: 'reports.tabs.businesses', icon: BriefcaseIcon },
  { id: 'progress', labelKey: 'reports.tabs.progress', icon: CalendarIcon },
  { id: 'financial', labelKey: 'reports.tabs.financial', icon: CreditCardIcon },
  { id: 'involved', labelKey: 'reports.tabs.involved', icon: UsersIcon },
  { id: 'hours', labelKey: 'reports.tabs.hours', icon: ClockIcon },
];

const STATUS_OPTIONS = ['all', ...PROGRESS_STATUS_KEYS];
const FINANCIAL_TYPES = ['all', 'credit', 'debit'];
const DEFAULT_FILTERS = {
  search: '',
  status: 'all',
  type: 'all',
  financialType: 'all',
  clientType: 'all',
  businessId: 'all',
  clientId: 'all',
  involvedId: 'all',
  businessType: '',
  responsible: '',
  startDate: '',
  endDate: '',
};
const EMPTY_LIST = [];
const emptyReport = () => Promise.resolve({ data: { data: [] } });
const REPORT_LOADERS = {
  clients: reportsAPI.clients,
  businesses: reportsAPI.businesses,
  progress: reportsAPI.progress,
  financial: reportsAPI.financial,
  involved: reportsAPI.involved,
  hours: reportsAPI.workedHours,
};

const badgeClass = (value) => {
  if (value === 'pre_initial' || value === 'both') return 'bg-slate-100 text-slate-700';
  if (value === 'started') return 'bg-sky-50 text-sky-700';
  if (value === 'in_progress' || value === 'business' || value === 'active' || value === 'credit' || value === 'completed') return 'bg-emerald-50 text-emerald-700';
  if (value === 'aborted' || value === 'canceled' || value === 'debit') return 'bg-rose-50 text-rose-700';
  return 'bg-amber-50 text-amber-700';
};

const date = (value, locale) => {
  return formatLocalizedDate(value, locale) || '-';
};

const valueText = (value) => (value === undefined || value === null || value === '' ? '-' : value);

const downloadFilename = (response, fallback) => {
  const disposition = response?.headers?.['content-disposition'] || '';
  const encodedMatch = disposition.match(/filename\*=UTF-8''([^;]+)/i);
  if (encodedMatch?.[1]) return decodeURIComponent(encodedMatch[1]);
  const plainMatch = disposition.match(/filename="?([^";]+)"?/i);
  return plainMatch?.[1] || fallback;
};

const getResponsibleName = (item = {}) => (
  item.responsibleInvolved?.client?.name ||
  item.responsibleInvolved?.involvedName ||
  item.responsibleContact?.name ||
  item.responsibleName ||
  item.workerName ||
  ''
);

const getInvolvedName = (item = {}) => item.client?.name || item.involvedName || '-';

const getClientBusinessRelations = (item = {}) => {
  if (Array.isArray(item.businessRelations)) return item.businessRelations;
  if (Array.isArray(item.relatedBusinesses)) return item.relatedBusinesses;
  return item.businesses || [];
};

const getClientBusinessCount = (item = {}) => {
  const relations = getClientBusinessRelations(item);
  if (relations.length) return new Set(relations.map((business) => business.businessId || business.id || business.name).filter(Boolean)).size;
  return Number(item.businessCount || item.businessesCount || 0);
};

const clientBusinessSummary = (item, t) => {
  const count = getClientBusinessCount(item);
  return count ? t('contacts.businessCount', { count }) : t('reports.contactOnly');
};

const getActiveFilterLabels = ({ filters, activeTab, t, language, clients, businesses, involvedRows }) => {
  const labels = [];
  if (filters.search?.trim()) labels.push(`${t('filters.search')}: ${filters.search.trim()}`);
  if (filters.status !== 'all' && ['progress', 'businesses'].includes(activeTab)) labels.push(`${t('filters.status')}: ${t(`status.${filters.status}`, filters.status)}`);
  if (filters.financialType !== 'all' && activeTab === 'financial') labels.push(`${t('filters.financialType')}: ${t(`financial.${filters.financialType}`, filters.financialType)}`);
  if (filters.clientType !== 'all' && activeTab === 'clients') labels.push(`${t('common.type')}: ${t(`contactTypes.${filters.clientType}`, filters.clientType)}`);
  if (filters.businessId !== 'all') labels.push(`${t('filters.business')}: ${businesses.find((item) => item.id === filters.businessId)?.name || filters.businessId}`);
  if (filters.clientId !== 'all') labels.push(`${t('filters.contact')}: ${clients.find((item) => item.id === filters.clientId)?.name || filters.clientId}`);
  if (filters.involvedId !== 'all') labels.push(`${t('filters.involved')}: ${getInvolvedName(involvedRows.find((item) => item.id === filters.involvedId)) || filters.involvedId}`);
  if (filters.businessType) labels.push(`${t('filters.businessType')}: ${filters.businessType}`);
  if (filters.responsible) labels.push(`${t('filters.responsible')}: ${filters.responsible}`);
  if (filters.startDate) labels.push(`${t('filters.startDate')}: ${formatLocalizedDate(filters.startDate, language)}`);
  if (filters.endDate) labels.push(`${t('filters.endDate')}: ${formatLocalizedDate(filters.endDate, language)}`);
  return labels;
};

const VisualizationPage = () => {
  const { t, i18n } = useTranslation();
  const { user } = useAuth();
  const formatDate = (value) => date(value, i18n.language);
  const [activeTab, setActiveTab] = useState('clients');
  const [filters, setFilters] = useState(DEFAULT_FILTERS);
  const [searchInput, setSearchInput] = useState('');
  const [reportData, setReportData] = useState([]);
  const [reportLoading, setReportLoading] = useState(true);
  const [reportUpdating, setReportUpdating] = useState(false);
  const [reportError, setReportError] = useState('');
  const [businessOptions, setBusinessOptions] = useState([]);
  const [clientOptions, setClientOptions] = useState([]);
  const [involvedOptions, setInvolvedOptions] = useState([]);
  const [selected, setSelected] = useState(null);
  const [exporting, setExporting] = useState('');
  const [detailExporting, setDetailExporting] = useState(false);
  const [exportError, setExportError] = useState('');
  const requestSequence = useRef(0);
  const hasLoadedReport = useRef(false);

  const changeActiveTab = (tabId) => {
    if (tabId === activeTab) return;
    requestSequence.current += 1;
    hasLoadedReport.current = false;
    setFilters(DEFAULT_FILTERS);
    setSearchInput('');
    setReportData([]);
    setReportLoading(true);
    setSelected(null);
    setActiveTab(tabId);
  };

  useEffect(() => {
    const timer = setTimeout(() => {
      setFilters((current) => (
        current.search === searchInput
          ? current
          : { ...current, search: searchInput }
      ));
    }, 350);
    return () => clearTimeout(timer);
  }, [searchInput]);

  useEffect(() => {
    let active = true;
    Promise.allSettled([
      reportsAPI.businesses(),
      reportsAPI.clients(),
      reportsAPI.involved(),
    ]).then(([businessesResult, clientsResult, involvedResult]) => {
      if (!active) return;
      if (businessesResult.status === 'fulfilled') setBusinessOptions(businessesResult.value.data?.data || []);
      if (clientsResult.status === 'fulfilled') setClientOptions(clientsResult.value.data?.data || []);
      if (involvedResult.status === 'fulfilled') setInvolvedOptions(involvedResult.value.data?.data || []);
    });
    return () => {
      active = false;
    };
  }, []);

  const apiParams = useMemo(() => ({
    search: filters.search || undefined,
    status: filters.status !== 'all' ? filters.status : undefined,
    type: filters.type !== 'all' ? filters.type : undefined,
    financialType: filters.financialType !== 'all' ? filters.financialType : undefined,
    clientType: filters.clientType !== 'all' ? filters.clientType : undefined,
    businessId: filters.businessId !== 'all' ? filters.businessId : undefined,
    clientId: filters.clientId !== 'all' ? filters.clientId : undefined,
    involvedId: filters.involvedId !== 'all' ? filters.involvedId : undefined,
    businessType: filters.businessType || undefined,
    responsible: filters.responsible || undefined,
    startDate: filters.startDate || undefined,
    endDate: filters.endDate || undefined,
  }), [filters]);

  useEffect(() => {
    const sequence = requestSequence.current + 1;
    requestSequence.current = sequence;
    const loader = REPORT_LOADERS[activeTab] || emptyReport;
    setReportError('');
    setReportUpdating(hasLoadedReport.current);
    if (!hasLoadedReport.current) setReportLoading(true);

    loader(apiParams)
      .then((response) => {
        if (requestSequence.current !== sequence) return;
        setReportData(response.data?.data || []);
        hasLoadedReport.current = true;
      })
      .catch((requestError) => {
        if (requestSequence.current !== sequence) return;
        setReportError(requestError.response?.data?.error || requestError.message || t('reports.loadError'));
      })
      .finally(() => {
        if (requestSequence.current !== sequence) return;
        setReportLoading(false);
        setReportUpdating(false);
      });
  }, [activeTab, apiParams, t]);

  const clients = activeTab === 'clients' ? reportData : EMPTY_LIST;
  const businesses = activeTab === 'businesses' ? reportData : EMPTY_LIST;
  const progress = activeTab === 'progress' ? reportData : EMPTY_LIST;
  const financial = activeTab === 'financial' ? reportData : EMPTY_LIST;
  const hours = activeTab === 'hours' ? reportData : EMPTY_LIST;
  const involved = activeTab === 'involved' ? reportData : EMPTY_LIST;

  const totals = useMemo(() => {
    const totalRevenue = financial.reduce((sum, item) => sum + Number(item.credits || 0), 0);
    const totalExpenses = financial.reduce((sum, item) => sum + Number(item.debits || 0), 0);
    const totalHours = hours.reduce((sum, item) => sum + Number(item.totalHours || 0), 0);
    const totalValue = hours.reduce((sum, item) => sum + Number(item.totalValue || 0), 0);
    return { totalRevenue, totalExpenses, balance: totalRevenue - totalExpenses, totalHours, totalValue };
  }, [financial, hours]);

  const progressRows = useMemo(() => (
    progress.flatMap((business) =>
      (business.progressList || []).map((item) => ({
        ...item,
        businessId: business.id,
        businessName: business.name,
        client: business.client,
      }))
    ).sort((a, b) => new Date(b.launchDate || 0) - new Date(a.launchDate || 0))
  ), [progress]);

  const involvedRows = useMemo(() => (
    involved.flatMap((business) =>
      (business.involvedPersons || []).map((item) => ({ ...item, businessName: business.name, businessClient: business.client }))
    )
  ), [involved]);
  const activeFilterLabels = useMemo(() => getActiveFilterLabels({
    filters,
    activeTab,
    t,
    language: i18n.language,
    clients: clientOptions,
    businesses: businessOptions,
    involvedRows: involvedOptions.flatMap((business) => (
      business.involvedPersons || []
    )),
  }), [activeTab, businessOptions, clientOptions, filters, i18n.language, involvedOptions, t]);

  const handleExport = async (format) => {
    setExportError('');
    setExporting(format);
    try {
      const kind = activeTab === 'clients' ? 'contacts' : activeTab;
      const { data } = await reportsAPI.export(kind, { ...apiParams, format, lang: i18n.language });
      const extension = format === 'xlsx' ? 'xls' : 'pdf';
      const mime = format === 'xlsx' ? 'application/vnd.ms-excel' : 'application/pdf';
      const blob = new Blob([data], { type: mime });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `relatorio-${kind}.${extension}`;
      link.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      setExportError(getApiErrorMessage(err, t('reports.exportError')));
    } finally {
      setExporting('');
    }
  };

  const handleDetailExport = async () => {
    if (!selected) return;
    const { kind, item } = selected;
    const reportKind = kind === 'client' ? 'contacts' : kind === 'business' ? 'businesses' : kind;
    const selectors = {
      client: { contactId: item.id },
      business: { businessId: item.id },
      progress: { progressId: item.id, businessId: item.businessId },
      financial: { businessId: item.businessId },
      involved: { involvedId: item.id, businessId: item.businessId },
      hours: { businessId: item.businessId },
    }[kind] || {};
    setExportError('');
    setDetailExporting(true);
    try {
      const response = await reportsAPI.export(reportKind, {
        ...apiParams,
        ...selectors,
        detail: true,
        format: 'pdf',
        lang: i18n.language,
      });
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      const selectorId = Object.values(selectors).find(Boolean) || item.id;
      link.download = downloadFilename(response, `detalhamento-${reportKind}-${selectorId}.pdf`);
      link.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      setExportError(getApiErrorMessage(err, t('reports.exportError')));
    } finally {
      setDetailExporting(false);
    }
  };

  if (reportLoading && !reportData.length) return <Spinner size="lg" />;

  return (
    <div className="mx-auto max-w-[1104px]">
      <div className="mb-7 flex flex-col gap-5 sm:flex-row sm:items-start sm:justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-tight text-slate-900">{t('reports.title')}</h1>
          <p className="mt-1 text-base text-slate-600">{t('reports.subtitle')}</p>
        </div>
        <div className="flex gap-2">
          <button className="inline-flex h-10 items-center justify-center gap-3 rounded-lg border border-slate-200 bg-white px-4 text-sm font-bold text-slate-900 shadow-sm disabled:opacity-60" type="button" onClick={() => handleExport('pdf')} disabled={Boolean(exporting)}>
            <BarChartIcon className="h-4 w-4" />
            {exporting === 'pdf' ? t('common.processing') : t('actions.exportPdf')}
          </button>
          <button className="inline-flex h-10 items-center justify-center gap-3 rounded-lg border border-slate-200 bg-white px-4 text-sm font-bold text-slate-900 shadow-sm disabled:opacity-60" type="button" onClick={() => handleExport('xlsx')} disabled={Boolean(exporting)}>
            <BarChartIcon className="h-4 w-4" />
            {exporting === 'xlsx' ? t('common.processing') : t('actions.exportExcel')}
          </button>
        </div>
      </div>

      {reportError && <Alert variant="error">{reportError}</Alert>}
      {exportError && <Alert variant="error">{exportError}</Alert>}

      <FiltersPanel
        activeTab={activeTab}
        filters={filters}
        setFilters={setFilters}
        searchInput={searchInput}
        setSearchInput={setSearchInput}
        updating={reportUpdating}
        t={t}
        clients={clientOptions}
        businesses={businessOptions}
        involvedRows={involvedOptions.flatMap((business) => (
          (business.involvedPersons || []).map((item) => ({ ...item, businessName: business.name }))
        ))}
      />

      {!!activeFilterLabels.length && (
        <section className="mb-5 rounded-lg border border-sky-100 bg-sky-50 p-3">
          <div className="flex flex-wrap items-center gap-2">
            {activeFilterLabels.map((label) => (
              <span key={label} className="rounded-full bg-white px-3 py-1 text-xs font-bold text-sky-700 shadow-sm">
                {t('filters.activeSelected', { filter: label })}
              </span>
            ))}
              <button type="button" className="px-2 text-xs font-bold text-sky-700" onClick={() => {
                setFilters(DEFAULT_FILTERS);
                setSearchInput('');
              }}>
              {t('filters.clear')}
            </button>
          </div>
        </section>
      )}

      <div className="mb-6 flex overflow-x-auto rounded-lg bg-slate-200/60 p-1">
        {TABS.map((tab) => {
          const Icon = tab.icon;
          const isActive = activeTab === tab.id;
          return (
            <button
              key={tab.id}
              type="button"
              className={`inline-flex h-8 shrink-0 items-center gap-2 rounded-md px-4 text-sm font-semibold transition ${
                isActive ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-700 hover:text-slate-900'
              }`}
              onClick={() => changeActiveTab(tab.id)}
            >
              <Icon className="h-4 w-4" />
              {t(tab.labelKey)}
            </button>
          );
        })}
      </div>

      {activeTab === 'clients' && <ClientsReport clients={clients} t={t} onSelect={setSelected} />}
      {activeTab === 'businesses' && <BusinessesReport businesses={businesses} user={user} t={t} onSelect={setSelected} />}
      {activeTab === 'progress' && <ProgressReport rows={progressRows} t={t} onSelect={setSelected} formatDate={formatDate} />}
      {activeTab === 'financial' && <FinancialReport financial={financial} totals={totals} t={t} onSelect={setSelected} />}
      {activeTab === 'involved' && <InvolvedReport rows={involvedRows} t={t} onSelect={setSelected} />}
      {activeTab === 'hours' && <HoursReport hours={hours} totals={totals} t={t} onSelect={setSelected} formatDate={formatDate} language={i18n.language} />}

      <DetailModal
        selected={selected}
        onClose={() => setSelected(null)}
        onExport={handleDetailExport}
        exporting={detailExporting}
        user={user}
        t={t}
        formatDate={formatDate}
        language={i18n.language}
      />
    </div>
  );
};

const FiltersPanel = ({
  activeTab,
  filters,
  setFilters,
  searchInput,
  setSearchInput,
  updating,
  t,
  clients,
  businesses,
  involvedRows,
}) => {
  const update = (event) => setFilters((current) => ({ ...current, [event.target.name]: event.target.value }));
  const resetFilters = () => {
    setFilters(DEFAULT_FILTERS);
    setSearchInput('');
  };
  return (
    <section className="mb-5 rounded-lg border border-slate-200 bg-white p-4 shadow-soft">
      <div className="mb-3 flex items-center justify-between gap-3">
        <h2 className="text-sm font-bold text-slate-900">{t('filters.title')}</h2>
        {updating && <span className="text-xs font-semibold text-sky-600">{t('filters.updating')}</span>}
      </div>
      <div className="grid gap-3 md:grid-cols-4">
        <label className="text-xs font-bold text-slate-600">
          {t('filters.search')}
          <input name="search" value={searchInput} onChange={(event) => setSearchInput(event.target.value)} className="mt-1 h-10 w-full rounded-lg border border-slate-200 px-3 text-sm text-slate-900" />
        </label>
        <label className="text-xs font-bold text-slate-600">
          {t('filters.business')}
          <select name="businessId" value={filters.businessId} onChange={update} className="mt-1 h-10 w-full rounded-lg border border-slate-200 px-3 text-sm">
            <option value="all">{t('filters.allBusinesses')}</option>
            {businesses.map((business) => <option key={business.id} value={business.id}>{business.name}</option>)}
          </select>
        </label>
        {['businesses', 'clients', 'progress', 'financial', 'hours'].includes(activeTab) && (
          <label className="text-xs font-bold text-slate-600">
            {t('filters.contact')}
            <select name="clientId" value={filters.clientId} onChange={update} className="mt-1 h-10 w-full rounded-lg border border-slate-200 px-3 text-sm">
              <option value="all">{t('filters.all')}</option>
              {clients.map((client) => <option key={client.id} value={client.id}>{client.name}</option>)}
            </select>
          </label>
        )}
        {['businesses', 'progress', 'financial', 'hours', 'involved'].includes(activeTab) && (
          <label className="text-xs font-bold text-slate-600">
            {t('filters.involved')}
            <select name="involvedId" value={filters.involvedId} onChange={update} className="mt-1 h-10 w-full rounded-lg border border-slate-200 px-3 text-sm">
              <option value="all">{t('filters.all')}</option>
              {involvedRows.map((item) => <option key={item.id} value={item.id}>{getInvolvedName(item)}</option>)}
            </select>
          </label>
        )}
        {(activeTab === 'progress' || activeTab === 'businesses') && (
          <label className="text-xs font-bold text-slate-600">
            {t('filters.status')}
            <select name="status" value={filters.status} onChange={update} className="mt-1 h-10 w-full rounded-lg border border-slate-200 px-3 text-sm">
              {STATUS_OPTIONS.map((status) => <option key={status} value={status}>{status === 'all' ? t('filters.all') : t(`status.${status}`)}</option>)}
            </select>
          </label>
        )}
        {activeTab === 'clients' && (
          <label className="text-xs font-bold text-slate-600">
            {t('common.type')}
            <select name="clientType" value={filters.clientType} onChange={update} className="mt-1 h-10 w-full rounded-lg border border-slate-200 px-3 text-sm">
              <option value="all">{t('filters.all')}</option>
              {['business', 'involved', 'both'].map((type) => <option key={type} value={type}>{t(`contactTypes.${type}`)}</option>)}
            </select>
          </label>
        )}
        {(activeTab === 'businesses' || activeTab === 'clients') && (
          <label className="text-xs font-bold text-slate-600">
            {t('filters.businessType')}
            <input name="businessType" value={filters.businessType} onChange={update} className="mt-1 h-10 w-full rounded-lg border border-slate-200 px-3 text-sm text-slate-900" />
          </label>
        )}
        {activeTab === 'financial' && (
          <label className="text-xs font-bold text-slate-600">
            {t('filters.financialType')}
            <select name="financialType" value={filters.financialType} onChange={update} className="mt-1 h-10 w-full rounded-lg border border-slate-200 px-3 text-sm">
              {FINANCIAL_TYPES.map((type) => <option key={type} value={type}>{type === 'all' ? t('filters.all') : t(`financial.${type}`)}</option>)}
            </select>
          </label>
        )}
        {['progress', 'financial', 'hours'].includes(activeTab) && (
          <label className="text-xs font-bold text-slate-600">
            {t('filters.responsible')}
            <input name="responsible" value={filters.responsible} onChange={update} className="mt-1 h-10 w-full rounded-lg border border-slate-200 px-3 text-sm text-slate-900" />
          </label>
        )}
        <DateField
          label={t('filters.startDate')}
          name="startDate"
          value={filters.startDate}
          onChange={(startDate) => setFilters((current) => ({ ...current, startDate }))}
          allowClear
        />
        <DateField
          label={t('filters.endDate')}
          name="endDate"
          value={filters.endDate}
          onChange={(endDate) => setFilters((current) => ({ ...current, endDate }))}
          min={filters.startDate}
          allowClear
        />
        <button
          type="button"
          className="mt-5 h-10 rounded-lg border border-slate-200 bg-slate-50 px-4 text-sm font-bold text-slate-700"
          onClick={resetFilters}
        >
          {t('filters.clear')}
        </button>
      </div>
    </section>
  );
};

const Shell = ({ title, subtitle, children }) => (
  <section className="overflow-hidden rounded-lg border border-slate-200 bg-white shadow-soft">
    <div className="px-4 py-5">
      <h2 className="font-bold text-slate-900">{title}</h2>
      {subtitle && <p className="mt-1 text-sm text-slate-600">{subtitle}</p>}
    </div>
    <div className="overflow-x-auto">{children}</div>
  </section>
);

const Th = ({ children }) => <th className="bg-slate-100 px-4 py-4 text-left font-semibold text-slate-700">{children}</th>;
const Td = ({ children, className = '' }) => <td className={`border-t border-slate-200 px-4 py-4 text-slate-700 ${className}`}>{children}</td>;

const Badge = ({ value, children }) => (
  <span className={`inline-flex rounded-full px-3 py-1 text-xs font-semibold ${badgeClass(value)}`}>
    {children}
  </span>
);

const LockedHint = ({ t, access }) => {
  const subscriptionLocked = isSubscriptionLock(access);
  const text = subscriptionLocked
    ? getSubscriptionRegularizationMessage(access?.subscription || {}, t, access?.plan)
    : t('plans.planLimitTitle');
  return (
  <span className="inline-flex max-w-[260px] items-center rounded-full bg-amber-50 px-3 py-1 text-xs font-bold text-amber-700">
    {text}
  </span>
  );
};

const ClickRow = ({ children, item, onSelect }) => (
  <tr className="cursor-pointer transition hover:bg-slate-50" onClick={() => onSelect(item)}>
    {children}
  </tr>
);

const ClientsReport = ({ clients, t, onSelect }) => (
  <Shell title={t('reports.contactsTitle')} subtitle={t('reports.contactsSubtitle')}>
    <table className="w-full min-w-[760px] text-sm">
      <thead>
        <tr>
          <Th>{t('common.name')}</Th>
          <Th>{t('common.type')}</Th>
          <Th>{t('auth.email')}</Th>
          <Th>{t('contacts.website')}</Th>
          <Th>{t('reports.tabs.businesses')}</Th>
        </tr>
      </thead>
      <tbody>
        {clients.map((item) => (
          <ClickRow key={item.id} item={{ kind: 'client', item }} onSelect={onSelect}>
            <Td className="font-bold text-slate-900">{item.name}</Td>
            <Td><Badge value={item.clientType}>{t(`contactTypes.${item.clientType}`, item.clientType)}</Badge></Td>
            <Td>{valueText(item.email)}</Td>
            <Td>{valueText(item.website)}</Td>
            <Td>{clientBusinessSummary(item, t)}</Td>
          </ClickRow>
        ))}
      </tbody>
    </table>
  </Shell>
);

const BusinessesReport = ({ businesses, user, t, onSelect }) => (
  <Shell title={t('reports.businessesTitle')} subtitle={t('reports.businessesSubtitle')}>
    <table className="w-full min-w-[820px] text-sm">
      <thead>
        <tr>
          <Th>{t('common.name')}</Th>
          <Th>{t('navigation.contacts')}</Th>
          <Th>{t('reports.balance')}</Th>
          <Th>{t('common.status')}</Th>
          <Th>{t('businessForm.typeLabel')}</Th>
        </tr>
      </thead>
      <tbody>
        {businesses.map((item) => {
          const balance = (item.financialEntries || []).reduce((sum, entry) => (
            entry.type === 'debit'
              ? sum - Number(entry.amount || 0)
              : sum + Number(entry.amount || 0)
          ), 0);
          const status = getDerivedBusinessStatus(item.progressList);
          const locked = Boolean(item.access?.locked);
          const selectedItem = { kind: 'business', item: { ...item, balance, status } };
          return (
            <React.Fragment key={item.id}>
              <ClickRow item={selectedItem} onSelect={onSelect}>
                <Td className="font-bold text-slate-900">{item.name}</Td>
                <Td>{getBusinessClientName(item, user, t)}</Td>
                <Td className={`font-bold ${balance < 0 ? 'text-red-500' : 'text-emerald-600'}`}>{locked ? <LockedHint t={t} access={item.access} /> : <MoneyValue value={balance} />}</Td>
                <Td>{locked ? <LockedHint t={t} access={item.access} /> : <Badge value={status}>{status ? t(`status.${status}`, status) : t('reports.noProgress')}</Badge>}</Td>
                <Td>{valueText(item.businessType)}</Td>
              </ClickRow>
              {!!item.description && (
                <tr className="cursor-pointer transition hover:bg-slate-50" onClick={() => onSelect(selectedItem)}>
                  <td colSpan={5} className="border-t border-slate-100 bg-slate-50/70 px-4 py-3 text-slate-700">
                    <div className="rounded-lg border border-slate-200 bg-white px-3 py-2">
                      <p className="text-xs font-bold text-slate-500">{t('businessDetails.descriptionTitle')}</p>
                      <p className="mt-1 text-sm leading-6 text-slate-700">{item.description}</p>
                    </div>
                  </td>
                </tr>
              )}
            </React.Fragment>
          );
        })}
      </tbody>
    </table>
  </Shell>
);

const ProgressReport = ({ rows, t, onSelect, formatDate }) => (
  <Shell title={t('reports.progressTitle')} subtitle={t('reports.progressSubtitle')}>
    <table className="w-full min-w-[860px] text-sm">
      <thead>
        <tr>
          <Th>{t('reports.tabs.businesses')}</Th>
          <Th>{t('common.status')}</Th>
          <Th>{t('common.date')}</Th>
          <Th>{t('progress.responsibleName')}</Th>
          <Th>{t('common.description')}</Th>
        </tr>
      </thead>
      <tbody>
        {rows.map((item) => (
          <ClickRow key={item.id} item={{ kind: 'progress', item }} onSelect={onSelect}>
            <Td className="font-bold text-slate-900">{item.businessName}</Td>
            <Td><Badge value={item.status}>{t(`status.${item.status}`, item.status)}</Badge></Td>
            <Td>{formatDate(item.launchDate)}</Td>
            <Td>{valueText(getResponsibleName(item))}</Td>
            <Td>{valueText(item.description)}</Td>
          </ClickRow>
        ))}
      </tbody>
    </table>
  </Shell>
);

const FinancialReport = ({ financial, totals, t, onSelect }) => (
  <div className="space-y-4">
    <div className="grid gap-4 md:grid-cols-3">
      <Metric label={t('reports.totalRevenue')} value={<MoneyValue value={totals.totalRevenue} />} className="text-emerald-600" />
      <Metric label={t('reports.totalExpenses')} value={<MoneyValue value={totals.totalExpenses} />} className="text-red-500" />
      <Metric label={t('reports.balance')} value={<MoneyValue value={totals.balance} />} className={totals.balance < 0 ? 'text-red-500' : 'text-emerald-600'} />
    </div>
    <Shell title={t('reports.financialByBusiness')} subtitle={t('reports.financialByBusinessSubtitle')}>
      <table className="w-full min-w-[720px] text-sm">
        <thead>
          <tr>
            <Th>{t('reports.tabs.businesses')}</Th>
            <Th>{t('reports.revenue')}</Th>
            <Th>{t('reports.expenses')}</Th>
            <Th>{t('reports.balance')}</Th>
            <Th>{t('reports.entries')}</Th>
          </tr>
        </thead>
        <tbody>
          {financial.map((item) => (
            <ClickRow key={item.businessId} item={{ kind: 'financial', item }} onSelect={onSelect}>
              <Td className="font-bold text-slate-900">{item.businessName}</Td>
              <Td className="font-bold text-emerald-600">{item.access?.locked ? <LockedHint t={t} access={item.access} /> : <MoneyValue value={item.credits} />}</Td>
              <Td className="font-bold text-red-500">{item.access?.locked ? <LockedHint t={t} access={item.access} /> : <MoneyValue value={item.debits} />}</Td>
              <Td className={`font-bold ${Number(item.balance || 0) < 0 ? 'text-red-500' : 'text-emerald-600'}`}>{item.access?.locked ? <LockedHint t={t} access={item.access} /> : <MoneyValue value={item.balance} />}</Td>
              <Td>{item.entries?.length || 0}</Td>
            </ClickRow>
          ))}
        </tbody>
      </table>
    </Shell>
  </div>
);

const InvolvedReport = ({ rows, t, onSelect }) => (
  <Shell title={t('reports.involvedTitle')} subtitle={t('reports.involvedSubtitle')}>
    <table className="w-full min-w-[720px] text-sm">
      <thead>
        <tr>
          <Th>{t('reports.tabs.businesses')}</Th>
          <Th>{t('common.name')}</Th>
          <Th>{t('involved.roleLabel')}</Th>
          <Th>{t('auth.email')}</Th>
        </tr>
      </thead>
      <tbody>
        {rows.map((item) => (
          <ClickRow key={item.id} item={{ kind: 'involved', item }} onSelect={onSelect}>
            <Td className="font-bold text-slate-900">{item.businessName}</Td>
            <Td>{getInvolvedName(item)}</Td>
            <Td><Badge value="both">{valueText(item.role)}</Badge></Td>
            <Td>{valueText(item.client?.email)}</Td>
          </ClickRow>
        ))}
      </tbody>
    </table>
  </Shell>
);

const HoursReport = ({ hours, totals, t, onSelect, formatDate, language }) => (
  <div className="space-y-4">
    <div className="grid gap-4 md:grid-cols-2">
      <Metric label={t('reports.totalHours')} value={`${Number(totals.totalHours || 0).toFixed(0)}h`} />
      <Metric label={t('reports.totalValue')} value={<MoneyValue value={totals.totalValue} />} className="text-sky-500" />
    </div>
    <Shell title={t('reports.hoursByBusiness')}>
      <div className="divide-y divide-slate-200">
        {hours.map((item) => (
          <button key={item.businessId} type="button" className="flex min-h-[74px] w-full items-center justify-between gap-4 px-4 py-3 text-left transition hover:bg-slate-50" onClick={() => onSelect({ kind: 'hours', item })}>
            <div>
              <p className="font-bold text-slate-900">{item.businessName}</p>
              <p className="mt-1 text-sm text-slate-600">{t('reports.hoursCount', { count: Number(item.totalHours || 0).toFixed(0) })}</p>
              {!!item.entries?.length && (
                <p className="mt-1 text-xs font-semibold text-slate-500">
                  {item.entries.map((entry) => [entry.serviceName, formatDate(entry.date), entry.time, `${formatMoneyNumber(entry.totalHours, { language })}h`].filter(Boolean).join(' - ')).filter(Boolean).join('; ')}
                </p>
              )}
            </div>
            <p className="font-bold text-sky-500">{item.access?.locked ? <LockedHint t={t} access={item.access} /> : <MoneyValue value={item.totalValue} />}</p>
          </button>
        ))}
      </div>
    </Shell>
  </div>
);

const DetailModal = ({ selected, onClose, onExport, exporting, user, t, formatDate, language }) => {
  if (!selected) return null;
  const { kind, item } = selected;
  const title = item.name || item.businessName || item.description || item.serviceName || getInvolvedName(item);
  const details = getDetails(kind, item, user, t, formatDate, language);
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-950/40 px-4" role="dialog" aria-modal="true">
      <section className="max-h-[88vh] w-full max-w-2xl overflow-y-auto rounded-lg bg-white shadow-xl">
        <div className="flex items-start justify-between gap-4 border-b border-slate-200 px-5 py-4">
          <div>
            <p className="text-xs font-bold uppercase tracking-wide text-sky-500">{t(`reports.detailKinds.${kind}`, kind)}</p>
            <h2 className="mt-1 text-xl font-bold text-slate-900">{title}</h2>
          </div>
          <div className="flex items-center gap-2">
            <button
              type="button"
              className="inline-flex h-9 items-center justify-center rounded-lg border border-slate-200 bg-white px-3 text-sm font-bold text-slate-800 disabled:opacity-60"
              onClick={onExport}
              disabled={exporting}
            >
              {exporting ? t('common.processing') : t('actions.exportPdf')}
            </button>
            <button type="button" aria-label={t('common.close', 'Fechar')} className="inline-flex h-9 w-9 items-center justify-center rounded-lg border border-slate-200 text-sm font-bold text-slate-700" onClick={onClose}>
              x
            </button>
          </div>
        </div>
        <div className="grid gap-3 p-5 sm:grid-cols-2">
          {item.access?.locked && (
            <div className="rounded-lg border border-amber-200 bg-amber-50 p-3 text-sm font-semibold text-amber-800 sm:col-span-2">
              {isSubscriptionLock(item.access)
                ? getSubscriptionRegularizationMessage(item.access?.subscription || {}, t, item.access?.plan)
                : t('plans.planLimitMessage')}
            </div>
          )}
          {details.map((detail) => (
            <div key={detail.label} className={`rounded-lg bg-slate-50 p-3 ${detail.wide ? 'sm:col-span-2' : ''}`}>
              <p className="text-xs font-bold text-slate-500">{detail.label}</p>
              <div className={`mt-1 text-sm font-semibold text-slate-900 ${detail.wide ? 'whitespace-pre-line leading-6' : ''}`}>{detail.value || '-'}</div>
            </div>
          ))}
        </div>
        {(kind === 'financial' || kind === 'hours') && (
          <div className="border-t border-slate-200 p-5">
            <h3 className="mb-3 font-bold text-slate-900">{kind === 'financial' ? t('financial.entries') : t('hours.entries')}</h3>
            <div className="divide-y divide-slate-200 rounded-lg border border-slate-200">
              {(item.entries || []).map((entry) => (
                <div key={entry.id} className="flex items-start justify-between gap-4 p-3 text-sm">
                  <div>
                    <p className="font-bold text-slate-900">{entry.description || entry.serviceName || '-'}</p>
                    <p className="text-slate-600">
                      {kind === 'hours'
                        ? [
                          `${t('common.date')}: ${formatDate(entry.date)}`,
                          `${t('hours.startTime')}: ${entry.time || '-'}`,
                          `${t('hours.totalHoursLabel')}: ${formatMoneyNumber(entry.totalHours, { language })}h`,
                          getResponsibleName(entry),
                        ].filter(Boolean).join(' - ')
                        : [formatDate(entry.launchDate || entry.date), getResponsibleName(entry)].filter(Boolean).join(' - ')}
                    </p>
                  </div>
                  <p className={entry.type === 'debit' ? 'font-bold text-red-500' : 'font-bold text-emerald-600'}>
                    <MoneyValue value={entry.amount ?? (Number(entry.hourlyRate || 0) * Number(entry.totalHours || 0))} />
                  </p>
                </div>
              ))}
            </div>
          </div>
        )}
        {kind === 'involved' && (
          <div className="border-t border-slate-200 p-5">
            <h3 className="mb-3 font-bold text-slate-900">{t('reports.relatedRecords')}</h3>
            <div className="grid gap-3 md:grid-cols-3">
              <RelatedList title={t('reports.tabs.progress')} items={item.progressEntries || []} getText={(entry) => `${entry.businessName || item.businessName || '-'} - ${t(`status.${entry.status}`, entry.status)} - ${formatDate(entry.launchDate || entry.date)} - ${entry.description || '-'}`} />
              <RelatedList title={t('reports.tabs.financial')} items={item.financialEntries || []} getText={(entry) => `${entry.businessName || item.businessName || '-'} - ${t(`financial.${entry.type}`, entry.type)} - ${formatDate(entry.launchDate || entry.date)} - ${formatMoneyNumber(entry.amount, { language })} - ${entry.description || '-'}`} />
              <RelatedList title={t('reports.tabs.hours')} items={item.workedHours || []} getText={(entry) => `${entry.businessName || item.businessName || '-'} - ${entry.serviceName || '-'} - ${formatDate(entry.date)}${entry.time ? ` ${entry.time}` : ''} - ${entry.totalHours || 0}h - ${formatMoneyNumber(Number(entry.hourlyRate || 0) * Number(entry.totalHours || 0), { language })}`} />
            </div>
          </div>
        )}
      </section>
    </div>
  );
};

const RelatedList = ({ title, items, getText }) => (
  <div className="rounded-lg border border-slate-200">
    <p className="border-b border-slate-200 px-3 py-2 text-sm font-bold text-slate-900">{title}</p>
    <div className="divide-y divide-slate-100">
      {items.length ? items.map((entry) => (
        <p key={entry.id} className="px-3 py-2 text-xs font-semibold text-slate-700">{getText(entry)}</p>
      )) : <p className="px-3 py-2 text-xs text-slate-500">-</p>}
    </div>
  </div>
);

const getDetails = (kind, item, user, t, formatDate = (value) => date(value, 'pt-BR'), language = 'pt-BR') => {
  if (kind === 'client') {
    return [
      { label: t('common.type'), value: t(`contactTypes.${item.clientType}`, item.clientType) },
      { label: t('clientForm.documentLabel'), value: item.document },
      { label: t('auth.email'), value: item.email },
      { label: t('profile.phone'), value: item.phone || item.whatsapp },
      { label: t('contacts.website'), value: item.website },
      { label: t('clientForm.activityFieldLabel'), value: item.activityField },
      { label: t('reports.tabs.businesses'), value: clientBusinessSummary(item, t) },
    ];
  }
  if (kind === 'business') {
    return [
      { label: t('navigation.contacts'), value: getBusinessClientName(item, user, t) },
      { label: t('common.status'), value: t(`status.${item.status}`, item.status) },
      { label: t('businessForm.typeLabel'), value: item.businessType },
      { label: t('businessDetails.startDate'), value: formatDate(item.startDate) },
      { label: t('businessDetails.endDate'), value: item.endDate ? formatDate(item.endDate) : t('businessDetails.open') },
      { label: t('reports.balance'), value: <span className={Number(item.balance || 0) < 0 ? 'text-red-500' : 'text-emerald-600'}><MoneyValue value={item.balance} /></span> },
      { label: t('businessDetails.descriptionTitle'), value: item.description, wide: true },
    ];
  }
  if (kind === 'progress') {
    return [
      { label: t('reports.tabs.businesses'), value: item.businessName },
      { label: t('navigation.contacts'), value: getBusinessClientName(item, user, t) },
      { label: t('common.status'), value: t(`status.${item.status}`, item.status) },
      { label: t('common.date'), value: formatDate(item.launchDate) },
      { label: t('progress.responsibleName'), value: getResponsibleName(item) },
      { label: t('common.description'), value: item.description },
    ];
  }
  if (kind === 'involved') {
    return [
      { label: t('reports.tabs.businesses'), value: item.businessName },
      { label: t('common.name'), value: getInvolvedName(item) },
      { label: t('involved.roleLabel'), value: item.role },
      { label: t('auth.email'), value: item.client?.email },
      { label: t('profile.phone'), value: item.client?.phone },
    ];
  }
  if (kind === 'financial') {
    return [
      { label: t('reports.tabs.businesses'), value: item.businessName },
      { label: t('navigation.contacts'), value: getBusinessClientName(item, user, t) },
      { label: t('reports.revenue'), value: <span className="text-emerald-600"><MoneyValue value={item.credits} /></span> },
      { label: t('reports.expenses'), value: <span className="text-red-500"><MoneyValue value={item.debits} /></span> },
      { label: t('reports.balance'), value: <span className={Number(item.balance || 0) < 0 ? 'text-red-500' : 'text-emerald-600'}><MoneyValue value={item.balance} /></span> },
      { label: t('reports.entries'), value: item.entries?.length || 0 },
    ];
  }
  if (kind === 'hours') {
    return [
      { label: t('reports.tabs.businesses'), value: item.businessName },
      { label: t('navigation.contacts'), value: getBusinessClientName(item, user, t) },
      { label: t('reports.totalHours'), value: `${formatMoneyNumber(item.totalHours, { language })}h` },
      { label: t('reports.totalValue'), value: <MoneyValue value={item.totalValue} /> },
      { label: t('reports.entries'), value: item.entries?.length || 0 },
    ];
  }
  return [
    { label: t('reports.tabs.businesses'), value: item.businessName },
    { label: t('navigation.contacts'), value: getBusinessClientName(item, user, t) },
    { label: t('reports.revenue'), value: <MoneyValue value={item.credits} /> },
    { label: t('reports.expenses'), value: <MoneyValue value={item.debits} /> },
    { label: t('reports.balance'), value: <MoneyValue value={item.balance} /> },
    { label: t('reports.totalHours'), value: `${formatMoneyNumber(item.totalHours, { language })}h` },
    { label: t('reports.totalValue'), value: <MoneyValue value={item.totalValue} /> },
    { label: t('reports.entries'), value: item.entries?.length || 0 },
  ];
};

const Metric = ({ label, value, className = 'text-slate-900' }) => (
  <div className="rounded-lg border border-slate-200 bg-white p-6 shadow-soft">
    <p className="text-sm text-slate-600">{label}</p>
    <p className={`mt-2 text-2xl font-bold ${className}`}>{value}</p>
  </div>
);

export default VisualizationPage;
